import client from './client';
import config from '../configLoader';
import { ProjectWorkList, ProjectWorkDetail, ProjectWorkSupervisorOption } from '../interfaces/ProjectWork';

const BASE = '/admin/2020ph/projectWork';

/** Tier labels the API stamps onto exports (client-configured, e.g. Rough/Finish). */
const tierLabelParams = (): { secondaryLabel: string; tertiaryLabel: string; fourthLabel: string; fifthLabel: string } => ({
  secondaryLabel: config.supervisorTierLabels?.secondary ?? 'Secondary',
  tertiaryLabel: config.supervisorTierLabels?.tertiary ?? 'Tertiary',
  fourthLabel: config.supervisorTierLabels?.fourth ?? 'Fourth',
  fifthLabel: config.supervisorTierLabels?.fifth ?? 'Fifth',
});

/** Projects with activity in the pay period (optionally filtered to one supervisor). */
export const getProjectWorkList = async (
  payPeriodID: number,
  supervisorEmployeeID?: number
): Promise<ProjectWorkList> => {
  const params: Record<string, number> = { payPeriodID };
  if (supervisorEmployeeID) params.supervisorEmployeeID = supervisorEmployeeID;
  const response = await client.get(`${BASE}/list`, { params });
  return response.data;
};

/** Full detail for one project + selected week. */
export const getProjectWorkDetail = async (
  projectID: number,
  payPeriodID: number
): Promise<ProjectWorkDetail> => {
  const response = await client.get(`${BASE}/project/${projectID}`, { params: { payPeriodID } });
  return response.data;
};

/**
 * Supervisors for the admin filter dropdown, scoped to the caller by the API.
 *
 * Previously this reused the shared `/admin/supervisors` list, which is org-wide
 * (every active employee whose Role has Supervisor = 1) — a scoped admin was offered
 * supervisors that `/list` would then reject with a 403 or return an empty table for.
 * The endpoint now returns only the supervisors that caller may actually select, already
 * sorted and name-formatted server-side.
 */
export const getProjectWorkSupervisors = async (): Promise<ProjectWorkSupervisorOption[]> => {
  const response = await client.get(`${BASE}/supervisors`);
  return response.data?.supervisors ?? [];
};

const triggerDownload = (data: BlobPart, mime: string, fileName: string): void => {
  const blob = new Blob([data], { type: mime });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Download an export. Scope-aware: pass projectID for a single project (detail view),
 * or omit it (optionally with supervisorEmployeeID) to export all visible projects.
 */
export const downloadProjectWorkExport = async (
  format: 'excel' | 'pdf',
  payPeriodID: number,
  opts: { projectID?: number; supervisorEmployeeID?: number } = {}
): Promise<void> => {
  const params: Record<string, string | number> = { payPeriodID, ...tierLabelParams() };
  if (opts.projectID) params.projectID = opts.projectID;
  if (opts.supervisorEmployeeID) params.supervisorEmployeeID = opts.supervisorEmployeeID;

  const response = await client.get(`${BASE}/export/${format}`, { params, responseType: 'blob' });

  const mime =
    format === 'excel'
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : 'application/pdf';
  const ext = format === 'excel' ? 'xlsx' : 'pdf';
  const scope = opts.projectID ? `Project${opts.projectID}` : 'AllProjects';
  triggerDownload(response.data, mime, `ProjectWork_${scope}_pp${payPeriodID}.${ext}`);
};
